/**
 * Colour utility functions for theme badges
 * Provides flexible colour manipulation (darken/lighten) with opacity control
 */

export interface ColorOptions {
  /** Percentage to darken (positive) or lighten (negative). Range: -100 to 100. Default: 0 */
  adjust?: number;
  /** Final opacity of the colour. Range: 0 to 1. Default: 1 */
  opacity?: number;
}

/**
 * Generate a modified hex colour with optional darkening/lightening and opacity
 * 
 * This function supports both darkening (positive adjust) and lightening (negative adjust).
 * 
 * @param color - Hex colour (e.g., "#ff0000" or "#f00")
 * @param options - Colour adjustment options
 * @returns RGBA colour string
 * 
 * @example
 * ```ts
 * // Darken by 30% with 0.5 opacity
 * genColorHex('#ff0000', { adjust: 30, opacity: 0.5 })
 * // => "rgba(178, 0, 0, 0.5)"
 * 
 * // Lighten by 30% (negative value)
 * genColorHex('#2B5F9E', { adjust: -30 })
 * // => "rgba(91, 143, 193, 1)"
 * 
 * // Just add opacity, no adjustment
 * genColorHex('#ff0000', { opacity: 0.7 })
 * // => "rgba(255, 0, 0, 0.7)"
 * 
 * // Background with slight lightening (typical use case for badges)
 * genColorHex(themeColor, { adjust: -10, opacity: 0.1 })
 * ```
 */
export function genColorHex(
  color: string | undefined | null,
  options: ColorOptions = {}
): string {
  const { adjust = 0, opacity = 1 } = options;

  // Fallback for invalid/missing colour
  if (!color) {
    return `rgba(0, 0, 0, ${opacity})`;
  }

  // Clean and validate hex colour
  const cleanHex = color.replace('#', '');

  // Support both 3-char (#f00) and 6-char (#ff0000) hex
  let fullHex: string;
  if (cleanHex.length === 3) {
    // Expand shorthand hex: f00 -> ff0000
    fullHex = cleanHex
      .split('')
      .map(char => char + char)
      .join('');
  } else if (cleanHex.length === 6) {
    fullHex = cleanHex;
  } else {
    console.warn(`[genColorHex] Invalid hex colour: ${color}`);
    return `rgba(0, 0, 0, ${opacity})`;
  }

  // Validate hex format
  if (!/^[0-9A-Fa-f]{6}$/.test(fullHex)) {
    console.warn(`[genColorHex] Invalid hex colour format: ${color}`);
    return `rgba(0, 0, 0, ${opacity})`;
  }

  // Parse RGB values
  const r = parseInt(fullHex.substring(0, 2), 16);
  const g = parseInt(fullHex.substring(2, 4), 16);
  const b = parseInt(fullHex.substring(4, 6), 16);

  // Apply adjustment (darken or lighten)
  let adjustedR: number, adjustedG: number, adjustedB: number;

  if (adjust > 0) {
    // Darken: move towards black (0, 0, 0)
    const factor = 1 - Math.min(adjust, 100) / 100;
    adjustedR = Math.round(r * factor);
    adjustedG = Math.round(g * factor);
    adjustedB = Math.round(b * factor);
  } else if (adjust < 0) {
    // Lighten: move towards white (255, 255, 255)
    const factor = Math.min(Math.abs(adjust), 100) / 100;
    adjustedR = Math.round(r + (255 - r) * factor);
    adjustedG = Math.round(g + (255 - g) * factor);
    adjustedB = Math.round(b + (255 - b) * factor);
  } else {
    // No adjustment
    adjustedR = r;
    adjustedG = g;
    adjustedB = b;
  }

  // Clamp values to valid range [0, 255]
  adjustedR = Math.max(0, Math.min(255, adjustedR));
  adjustedG = Math.max(0, Math.min(255, adjustedG));
  adjustedB = Math.max(0, Math.min(255, adjustedB));

  // Clamp opacity to valid range [0, 1]
  const clampedOpacity = Math.max(0, Math.min(1, opacity));

  return `rgba(${adjustedR}, ${adjustedG}, ${adjustedB}, ${clampedOpacity})`;
}
